import type { FlowType } from "@lending/navigation/types";
import type { MembershipShareAccessResponse, StateEnum } from "@lending/types";

const MEMBERSHIP_SELECTION_STEP_IDS = new Set([
  "membership-share-select",
  "membership-share-use-different-email",
  "membership-share-verify-email",
]);

const MEMBERSHIP_SHARE_PENDING_STEP_IDS = new Set([
  "membership-share-use-different-email",
  "membership-share-verify-email",
  "membership-share-activate",
]);

const LINKED_MEMBERSHIP_SHARE_PENDING_STEP_IDS = new Set([
  "membership-share-disclosures",
  "membership-share-processing",
  "membership-share-documents-requested",
  "membership-share-submitting-documents",
  "membership-share-status",
]);

const LINKED_POST_MEMBERSHIP_STATUS_STATES = new Set<StateEnum>([
  "membership_share_failed",
  "submitted",
  "los_processing",
  "los_processing_paused_memo_mode",
  "los_processing_failed",
  "completed",
]);

function isMembershipShareState(applicationState?: StateEnum) {
  return (
    applicationState === "membership_selection" || applicationState === "membership_share_pending"
  );
}

function getMembershipShareStepId(
  step: "select" | "use-different-email" | "verify-email" | "activate"
): string {
  return `membership-share-${step}`;
}

function getMembershipShareCanonicalStepId(
  applicationState: StateEnum,
  currentStepId: string | null,
  accountApplicationUuid?: string | null,
  access?: MembershipShareAccessResponse,
  flowType: FlowType = "web"
) {
  if (flowType === "olb") {
    if (accountApplicationUuid && LINKED_POST_MEMBERSHIP_STATUS_STATES.has(applicationState)) {
      return "submitted";
    }

    if (
      applicationState === "membership_selection" ||
      applicationState === "membership_share_pending"
    ) {
      return "submitted";
    }

    return undefined;
  }

  if (access) {
    if (access.email_conflict) {
      return currentStepId === getMembershipShareStepId("use-different-email")
        ? null
        : getMembershipShareStepId("use-different-email");
    }

    if (access.authentication_required) {
      return currentStepId === getMembershipShareStepId("verify-email")
        ? null
        : getMembershipShareStepId("verify-email");
    }

    if (applicationState === "membership_selection") {
      return currentStepId === getMembershipShareStepId("select")
        ? null
        : getMembershipShareStepId("select");
    }

    if (applicationState === "membership_share_pending") {
      if (accountApplicationUuid) {
        return currentStepId !== null && LINKED_MEMBERSHIP_SHARE_PENDING_STEP_IDS.has(currentStepId)
          ? null
          : "membership-share-disclosures";
      }

      return currentStepId === getMembershipShareStepId("activate")
        ? null
        : getMembershipShareStepId("activate");
    }
  }

  if (applicationState === "membership_selection") {
    return currentStepId !== null && MEMBERSHIP_SELECTION_STEP_IDS.has(currentStepId)
      ? null
      : "membership-share-select";
  }

  if (applicationState === "membership_share_pending") {
    if (accountApplicationUuid) {
      return currentStepId !== null && LINKED_MEMBERSHIP_SHARE_PENDING_STEP_IDS.has(currentStepId)
        ? null
        : "membership-share-disclosures";
    }

    return currentStepId !== null && MEMBERSHIP_SHARE_PENDING_STEP_IDS.has(currentStepId)
      ? null
      : "membership-share-activate";
  }

  if (accountApplicationUuid && LINKED_POST_MEMBERSHIP_STATUS_STATES.has(applicationState)) {
    return "membership-share-status";
  }

  return undefined;
}

export { getMembershipShareCanonicalStepId, getMembershipShareStepId, isMembershipShareState };
